import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  /** Toast を表示。auto-dismiss は durationMs (default 4000) 後 + click でも閉じる。 */
  toast: (type: ToastType, message: string, durationMs?: number) => void;
}

const ToastContext = createContext<ToastContextValue>({ toast: () => {} });

export function useToast(): ToastContextValue {
  return useContext(ToastContext);
}

const TOAST_BG: Record<ToastType, string> = {
  success: 'bg-success-600',
  error: 'bg-error-600',
  warning: 'bg-warning-600',
  info: 'bg-info-600',
};

/**
 * Toast provider — top-right に積み上げ、4 秒 auto-dismiss、click でも閉じる。
 * 旧 Toast.tsx (L2 参照、docs/97 §3) の context pattern 踏襲、
 * color は shared-ui preset の semantic palette に統一 (旧の生 green-600/red-600 から差し替え)。
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextIdRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback<ToastContextValue['toast']>(
    (type, message, durationMs = 4000) => {
      const id = nextIdRef.current++;
      setItems((prev) => [...prev, { id, type, message }]);
      if (durationMs > 0) {
        setTimeout(() => dismiss(id), durationMs);
      }
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="fixed top-4 right-4 z-[9999] grid gap-2 max-w-sm pointer-events-none"
        aria-live="polite"
      >
        {items.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => dismiss(t.id)}
            className={`pointer-events-auto px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white text-left ${TOAST_BG[t.type]}`}
          >
            {t.message}
          </button>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
